// frontend/src/components/automation/StepViewer.jsx - Enhanced with scroll effects
import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  ChevronUp, 
  ChevronDown, 
  Play, 
  Pause,
  RotateCcw,
  Filter,
  Search,
  ArrowDown,
  ArrowUp,
  Activity,
  Globe,
  MousePointer,
  Type,
  Eye,
  EyeOff
} from 'lucide-react'

const StepViewer = ({ steps = [] }) => {
  const [autoScroll, setAutoScroll] = useState(true)
  const [filterStatus, setFilterStatus] = useState('all') // 'all', 'success', 'error', 'pending'
  const [showDetails, setShowDetails] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showScrollButtons, setShowScrollButtons] = useState(false)
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(false)
  
  const containerRef = useRef(null)
  const latestStepRef = useRef(null)

  // Check scroll position and update button states
  const checkScrollButtons = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current
      setCanScrollUp(scrollTop > 0)
      setCanScrollDown(scrollTop < scrollHeight - clientHeight - 1)
      setShowScrollButtons(scrollHeight > clientHeight)
    }
  }

  // Auto-scroll to latest step
  useEffect(() => {
    if (autoScroll && latestStepRef.current && steps.length > 0) {
      latestStepRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      })
    }
  }, [steps, autoScroll])

  // Filter and search logic - MOVED UP to define before use
  const filteredSteps = steps.filter(step => {
    const matchesSearch = step.thoughts?.next_goal?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         step.url?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         step.action?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatus = filterStatus === 'all' || step.status === filterStatus
    
    return matchesSearch && matchesStatus
  })

  // Update scroll buttons when steps change
  useEffect(() => {
    checkScrollButtons()
    
    const container = containerRef.current
    if (container) {
      container.addEventListener('scroll', checkScrollButtons)
      return () => container.removeEventListener('scroll', checkScrollButtons)
    }
  }, [steps, filteredSteps])

  // Smooth scroll functions
  const scrollUp = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        top: -200,
        behavior: 'smooth'
      })
    }
  }

  const scrollDown = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        top: 200,
        behavior: 'smooth'
      })
    }
  }

  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }
  }

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth'
      })
    }
  }

  // Jump to specific step types
  const scrollToFirstOfStatus = (status) => {
    const index = filteredSteps.findIndex(step => step.status === status)
    if (index !== -1) {
      scrollToStep(index)
    }
  }

  const scrollToStep = (index) => {
    if (containerRef.current) {
      const container = containerRef.current
      const stepElements = container.querySelectorAll('[data-step-index]')
      const targetElement = stepElements[index]
      
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      }
    }
  }

  // Get step action icon
  const getActionIcon = (step) => {
    const action = step.action?.toLowerCase() || step.thoughts?.next_goal?.toLowerCase() || ''
    
    if (action.includes('click')) return <MousePointer className="h-4 w-4 text-blue-500" />
    if (action.includes('type') || action.includes('input')) return <Type className="h-4 w-4 text-green-500" />
    if (action.includes('navigate') || action.includes('go to')) return <Globe className="h-4 w-4 text-purple-500" />
    if (action.includes('scroll')) return <Activity className="h-4 w-4 text-orange-500" />
    
    return <Activity className="h-4 w-4 text-gray-500" />
  }

  // Status counts for filtering
  const statusCounts = {
    all: filteredSteps.length,
    success: filteredSteps.filter(step => step.status === 'success').length,
    error: filteredSteps.filter(step => step.status === 'error').length,
    pending: filteredSteps.filter(step => !step.status || step.status === 'pending').length
  }

  if (steps.length === 0) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center text-gray-500 py-8"
      >
        <motion.div
          animate={{ y: [0, -5, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-gray-100 to-slate-100 rounded-2xl flex items-center justify-center"
        >
          <Clock className="h-8 w-8 text-gray-300" />
        </motion.div>
        <p className="font-semibold text-lg mb-1">No execution steps yet</p>
        <p className="text-sm text-gray-400">Execution steps will appear here as tests run</p>
      </motion.div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Controls Header */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between bg-gray-50 rounded-lg p-3">
        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-2 flex-1">
          {/* Search */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search steps..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            )}
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All ({statusCounts.all})</option>
            <option value="success">Success ({statusCounts.success})</option>
            <option value="error">Error ({statusCounts.error})</option>
            <option value="pending">Pending ({statusCounts.pending})</option>
          </select>
        </div>

        {/* Control Buttons */}
        <div className="flex items-center space-x-2">
          {/* Auto-scroll Toggle */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setAutoScroll(!autoScroll)}
            className={`px-3 py-2 text-xs rounded-lg border transition-all ${
              autoScroll 
                ? 'bg-blue-100 text-blue-700 border-blue-300' 
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
            title="Auto-scroll to latest step"
          >
            {autoScroll ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
          </motion.button>

          {/* Details Toggle */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowDetails(!showDetails)}
            className={`px-3 py-2 text-xs rounded-lg border transition-all ${
              showDetails 
                ? 'bg-green-100 text-green-700 border-green-300' 
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
            title="Toggle step details"
          >
            {showDetails ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
          </motion.button>

          {/* Quick Navigation */}
          {statusCounts.error > 0 && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToFirstOfStatus('error')}
              className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded border border-red-200"
              title="Jump to first error"
            >
              Error
            </motion.button>
          )}
        </div>
      </div>

      {/* Steps Container with Scroll Effects */}
      <div className="relative bg-white rounded-lg border border-gray-200 shadow-sm">
        {/* Scroll Navigation */}
        {showScrollButtons && (
          <div className="absolute right-2 top-2 z-20 flex flex-col space-y-1">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={scrollUp}
              disabled={!canScrollUp}
              className={`p-1 rounded border transition-all ${
                canScrollUp 
                  ? 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300 hover:border-gray-400 shadow-sm' 
                  : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
              }`}
              title="Scroll up"
            >
              <ChevronUp className="h-3 w-3" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={scrollDown}
              disabled={!canScrollDown}
              className={`p-1 rounded border transition-all ${
                canScrollDown 
                  ? 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300 hover:border-gray-400 shadow-sm' 
                  : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
              }`}
              title="Scroll down"
            >
              <ChevronDown className="h-3 w-3" />
            </motion.button>
          </div>
        )}

        {/* Quick Jump Buttons */}
        {showScrollButtons && (
          <div className="absolute left-2 top-2 z-20 flex space-x-1">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={scrollToTop}
              className="p-1 bg-white hover:bg-gray-50 text-gray-600 border border-gray-300 rounded shadow-sm"
              title="Jump to top"
            >
              <ArrowUp className="h-3 w-3" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={scrollToBottom}
              className="p-1 bg-white hover:bg-gray-50 text-gray-600 border border-gray-300 rounded shadow-sm"
              title="Jump to bottom"
            >
              <ArrowDown className="h-3 w-3" />
            </motion.button>
          </div>
        )}

        {/* Scrollable Steps List */}
        <div 
          ref={containerRef}
          className="max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 p-4"
          style={{ scrollBehavior: 'smooth' }}
        >
          <div className="space-y-3">
            <AnimatePresence>
              {filteredSteps.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center text-gray-500 py-8"
                >
                  <Filter className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="font-medium">No steps match your criteria</p>
                  <p className="text-sm mt-1">Try adjusting your search or filters</p>
                </motion.div>
              ) : (
                filteredSteps.map((step, index) => {
                  const isLatest = index === filteredSteps.length - 1
                  const isError = step.status === 'error'
                  const isSuccess = step.status === 'success'
                  
                  return (
                    <motion.div
                      key={`${step.step_number}-${index}`}
                      ref={isLatest ? latestStepRef : null}
                      data-step-index={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ scale: 1.01, y: -1 }}
                      className={`p-4 border rounded-lg transition-all duration-300 ${
                        isError ? 'border-red-200 bg-red-50 hover:bg-red-100' :
                        isSuccess ? 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100' :
                        'border-gray-200 bg-gray-50 hover:bg-gray-100'
                      } ${isLatest && autoScroll ? 'ring-2 ring-blue-300 ring-opacity-50' : ''}`}
                    >
                      <div className="flex items-start space-x-3">
                        {/* Status and Action Icons */}
                        <div className="flex flex-col items-center space-y-1">
                          {/* Status Icon */}
                          <div className="flex-shrink-0">
                            {step.status === 'success' ? (
                              <CheckCircle className="h-5 w-5 text-emerald-500" />
                            ) : step.status === 'error' ? (
                              <XCircle className="h-5 w-5 text-red-500" />
                            ) : (
                              <Clock className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                          
                          {/* Action Icon */}
                          {showDetails && (
                            <div className="flex-shrink-0">
                              {getActionIcon(step)}
                            </div>
                          )}
                        </div>

                        {/* Step Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-medium text-gray-900">
                              Step {step.step_number}
                              {step.action && (
                                <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                  {step.action}
                                </span>
                              )}
                            </p>
                            <div className="text-xs text-gray-500">
                              {step.timestamp && new Date(step.timestamp).toLocaleTimeString()}
                            </div>
                          </div>
                          
                          <p className="text-sm text-gray-600 mb-2 leading-relaxed">
                            {step.thoughts?.next_goal || step.description || 'Processing...'}
                          </p>
                          
                          {/* Additional Details */}
                          {showDetails && (
                            <div className="space-y-2">
                              {step.url && (
                                <div className="flex items-center space-x-2">
                                  <Globe className="h-3 w-3 text-gray-400" />
                                  <p className="text-xs text-gray-500 truncate">
                                    {step.url}
                                  </p>
                                </div>
                              )}
                              
                              {step.selector && (
                                <div className="text-xs text-gray-500 bg-gray-100 rounded px-2 py-1 font-mono">
                                  Selector: {step.selector}
                                </div>
                              )}
                              
                              {step.value && (
                                <div className="text-xs text-gray-500 bg-blue-50 rounded px-2 py-1">
                                  Value: {step.value}
                                </div>
                              )}
                              
                              {step.error && (
                                <div className="text-xs text-red-600 bg-red-100 rounded px-2 py-1">
                                  Error: {step.error}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Step Number Badge */}
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          isError ? 'bg-red-200 text-red-800' :
                          isSuccess ? 'bg-emerald-200 text-emerald-800' :
                          'bg-gray-200 text-gray-800'
                        }`}>
                          {step.step_number}
                        </div>
                      </div>
                    </motion.div>
                  )
                })
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Gradient Fade Effects */}
        {showScrollButtons && (
          <>
            {canScrollUp && (
              <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-white to-transparent pointer-events-none z-10" />
            )}
            {canScrollDown && (
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent pointer-events-none z-10" />
            )}
          </>
        )}

        {/* Progress Indicator */}
        {showScrollButtons && (
          <div className="absolute right-1 top-1/2 transform -translate-y-1/2 z-10">
            <div className="w-1 h-24 bg-gray-200 rounded-full relative">
              <motion.div
                className="w-full bg-blue-500 rounded-full absolute top-0"
                style={{
                  height: `${Math.min(100, (containerRef.current?.clientHeight / containerRef.current?.scrollHeight) * 100)}%`,
                  top: `${(containerRef.current?.scrollTop / (containerRef.current?.scrollHeight - containerRef.current?.clientHeight)) * 70}%`
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="flex items-center justify-between text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
        <div className="flex items-center space-x-4">
          <span>
            {filteredSteps.length} of {steps.length} steps
            {filteredSteps.length !== steps.length && ' (filtered)'}
          </span>
          
          {statusCounts.success > 0 && (
            <span className="text-emerald-600">
              ✓ {statusCounts.success} successful
            </span>
          )}
          
          {statusCounts.error > 0 && (
            <span className="text-red-600">
              ✗ {statusCounts.error} errors
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <span className={`w-2 h-2 rounded-full ${autoScroll ? 'bg-blue-500' : 'bg-gray-400'}`}></span>
          <span>Auto-scroll {autoScroll ? 'on' : 'off'}</span>
        </div>
      </div>
    </div>
  )
}

export default StepViewer